'use client';

import { c as _c } from "react/compiler-runtime";
import React, { type ReactNode, type RefObject } from 'react';
import { Button } from '@/app/components/button';
import { Divider } from '@/app/components/divider';
import { Radio } from '@/app/components/forms/radio';
import { TextField } from '@/app/components/forms/text-field';
import { INTEREST_TYPE_TIL, MAX_INTEREST, MAX_PERCENT, MIN_PERCENT } from '@/app/constants/calculator';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { ColorSelection } from '@/app/lcv/components/sections/calculator/configure/color-selection';
import { GradeSelection } from '@/app/lcv/components/sections/calculator/configure/grade-selection';
import { ModelCategorySelection } from '@/app/lcv/components/sections/calculator/configure/model-category-selection';
import type { CalculatorConfigProps } from '@/app/lcv/components/sections/calculator/types';
import { formatPayment, formatPercent, getMinTilDownPaymentPercent, getTilInterestByModel, getValidatedPaymentValue, limit2Float, removeDuplicateDot, removeEndWithDot, removeFirstDot } from '@/app/lcv/components/sections/calculator/utility';
import { type Month } from '@/app/lcv/types/calculator';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';
import type { LcvModelSubCategory } from '@/types/tis-cms/types';
interface CustomLabelContentProps {
  label: ReactNode;
  content: ReactNode;
}
const CustomLabelContent = t0 => {
  const $ = _c(5);
  const {
    label,
    content
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="font-noto-bold">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== content || $[3] !== t1) {
    t2 = <div className="text-body-1-fixed grid gap-y-2">{t1}{content}</div>;
    $[2] = content;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export interface ConfigureFormContentProps extends CalculatorConfigProps {
  sentinelRef: RefObject<HTMLDivElement | null>;
}
export const ConfigureFormContent = t0 => {
  const $ = _c(140);
  const {
    listModelCategory: t1,
    listModelSubCategory: t2,
    listModel: t3,
    listColor: t4,
    listMonth: t5,
    listInterestType: t6,
    modelCategory,
    modelSubCategory,
    model,
    color,
    month,
    interestType: t7,
    percent: t8,
    payment: t9,
    price: t10,
    interest: t11,
    tilVehicleModel,
    onSelectCategory,
    onSelectModelSubCategory,
    onSelectModel,
    onSelectColor,
    onChangeMonth,
    onChangeInterestType,
    onChangePercent,
    onChangePayment,
    onChangeInterest,
    onSubmit,
    sentinelRef
  } = t0;
  let t12;
  if ($[0] !== t1) {
    t12 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t12;
  } else {
    t12 = $[1];
  }
  const listModelCategory = t12;
  let t13;
  if ($[2] !== t2) {
    t13 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t13;
  } else {
    t13 = $[3];
  }
  const listModelSubCategory = t13;
  let t14;
  if ($[4] !== t3) {
    t14 = t3 === undefined ? [] : t3;
    $[4] = t3;
    $[5] = t14;
  } else {
    t14 = $[5];
  }
  const listModel = t14;
  let t15;
  if ($[6] !== t4) {
    t15 = t4 === undefined ? [] : t4;
    $[6] = t4;
    $[7] = t15;
  } else {
    t15 = $[7];
  }
  const listColor = t15;
  let t16;
  if ($[8] !== t5) {
    t16 = t5 === undefined ? [] : t5;
    $[8] = t5;
    $[9] = t16;
  } else {
    t16 = $[9];
  }
  const listMonth = t16;
  const listInterestType = t6 === undefined ? [] : t6;
  let t17;
  if ($[10] !== t7) {
    t17 = t7 === undefined ? {
      label: INTEREST_TYPE_TIL,
      value: INTEREST_TYPE_TIL
    } : t7;
    $[10] = t7;
    $[11] = t17;
  } else {
    t17 = $[11];
  }
  const interestType = t17;
  const percent = t8 === undefined ? "0" : t8;
  const payment = t9 === undefined ? "0" : t9;
  const price = t10 === undefined ? "0" : t10;
  const interest = t11 === undefined ? "0" : t11;
  const [isOpenCategory, t18] = useDisclosure(false);
  const {
    toggle: toggleCategory
  } = t18;
  const [isOpenModel, t19] = useDisclosure(false);
  const {
    toggle: toggleModel
  } = t19;
  const [isOpenColor, t20] = useDisclosure(false);
  const {
    toggle: toggleColor
  } = t20;
  let t21;
  if ($[12] !== onChangePercent) {
    t21 = e => {
      let value = e.target.value;
      value = value.replace(/[^0-9.]/, "");
      value = removeDuplicateDot(value);
      value = limit2Float(value);
      if (parseFloat(value) > MAX_PERCENT) {
        value = MAX_PERCENT.toString();
      }
      safeCall(onChangePercent, value);
    };
    $[12] = onChangePercent;
    $[13] = t21;
  } else {
    t21 = $[13];
  }
  const onPercentChange = t21;
  let t22;
  if ($[14] !== interestType.value || $[15] !== modelCategory || $[16] !== modelSubCategory || $[17] !== onChangePercent || $[18] !== tilVehicleModel) {
    t22 = e_0 => {
      let value_0 = e_0.target.value;
      value_0 = removeFirstDot(value_0);
      value_0 = removeEndWithDot(value_0);
      safeCall(onChangePercent, parseFloat(value_0) < parseFloat(MIN_PERCENT) ? MIN_PERCENT : value_0);
      const TIL_INTEREST_BY_MODEL = getTilInterestByModel(modelCategory, modelSubCategory, tilVehicleModel);
      const MIN_TIL_PERCENT = getMinTilDownPaymentPercent(TIL_INTEREST_BY_MODEL);
      const minPercent = interestType.value === INTEREST_TYPE_TIL ? MIN_TIL_PERCENT?.toString() ?? MIN_PERCENT : MIN_PERCENT;
      safeCall(onChangePercent, parseFloat(value_0) < parseFloat(minPercent) ? minPercent : value_0);
    };
    $[14] = interestType.value;
    $[15] = modelCategory;
    $[16] = modelSubCategory;
    $[17] = onChangePercent;
    $[18] = tilVehicleModel;
    $[19] = t22;
  } else {
    t22 = $[19];
  }
  const onPercentBlur = t22;
  let t23;
  if ($[20] !== onChangePayment || $[21] !== price) {
    t23 = e_1 => {
      const value_1 = getValidatedPaymentValue(e_1.target.value, price);
      safeCall(onChangePayment, value_1);
    };
    $[20] = onChangePayment;
    $[21] = price;
    $[22] = t23;
  } else {
    t23 = $[22];
  }
  const onPaymentChange = t23;
  let t24;
  if ($[23] !== interestType.value || $[24] !== modelCategory || $[25] !== modelSubCategory || $[26] !== onChangePayment || $[27] !== price || $[28] !== tilVehicleModel) {
    t24 = e_2 => {
      const value_2 = getValidatedPaymentValue(e_2.target.value);
      const TIL_INTEREST_BY_MODEL_0 = getTilInterestByModel(modelCategory, modelSubCategory, tilVehicleModel);
      const MIN_TIL_PERCENT_0 = getMinTilDownPaymentPercent(TIL_INTEREST_BY_MODEL_0) ?? parseFloat(MIN_PERCENT);
      const MIN_TIL_PRICE = parseFloat(price.toString()) / 100 * MIN_TIL_PERCENT_0;
      if (parseFloat(value_2) < MIN_TIL_PRICE && interestType.value === INTEREST_TYPE_TIL) {
        safeCall(onChangePayment, MIN_TIL_PRICE.toString());
      }
    };
    $[23] = interestType.value;
    $[24] = modelCategory;
    $[25] = modelSubCategory;
    $[26] = onChangePayment;
    $[27] = price;
    $[28] = tilVehicleModel;
    $[29] = t24;
  } else {
    t24 = $[29];
  }
  const onPaymentBlur = t24;
  let t25;
  if ($[30] !== interest || $[31] !== onChangeInterest) {
    t25 = () => {
      if (!isNaN(parseFloat(interest))) {
        let value_3 = parseFloat(interest);
        value_3 = value_3 + 0.1;
        value_3 = Math.min(value_3, MAX_INTEREST);
        safeCall(onChangeInterest, value_3.toFixed(2));
      }
    };
    $[30] = interest;
    $[31] = onChangeInterest;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  const onInterestUp = t25;
  let t26;
  if ($[33] !== interest || $[34] !== onChangeInterest) {
    t26 = () => {
      if (!isNaN(parseFloat(interest))) {
        let value_4 = parseFloat(interest);
        value_4 = value_4 - 0.1;
        value_4 = Math.max(value_4, 0);
        safeCall(onChangeInterest, value_4.toFixed(2));
      }
    };
    $[33] = interest;
    $[34] = onChangeInterest;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  const onInterestDown = t26;
  let t27;
  if ($[36] !== onChangeInterest) {
    t27 = e_3 => {
      let value_5 = e_3.target.value;
      value_5 = value_5.replace(/[^0-9.]/, "");
      value_5 = removeDuplicateDot(value_5);
      value_5 = limit2Float(value_5);
      if (parseFloat(value_5) > MAX_INTEREST) {
        value_5 = MAX_INTEREST.toString();
      }
      safeCall(onChangeInterest, value_5);
    };
    $[36] = onChangeInterest;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  const onInterestRateFieldChange = t27;
  const onInterestChange = value_6 => {
    const type = listInterestType.filter(o => o.value === value_6);
    if (type.length) {
      safeCall(onChangeInterestType, type[0]);
    }
  };
  let t28;
  if ($[38] !== onChangeInterest) {
    t28 = e_4 => {
      let value_7 = e_4.target.value;
      value_7 = removeFirstDot(value_7);
      value_7 = removeEndWithDot(value_7);
      safeCall(onChangeInterest, value_7);
    };
    $[38] = onChangeInterest;
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  const onBlurInterest = t28;
  let t29;
  if ($[40] !== listModelSubCategory || $[41] !== onSelectModelSubCategory) {
    t29 = value_8 => {
      const modelSub = listModelSubCategory.filter(o_0 => o_0.documentId === value_8);
      if (modelSub.length) {
        safeCall(onSelectModelSubCategory, modelSub[0]);
      }
    };
    $[40] = listModelSubCategory;
    $[41] = onSelectModelSubCategory;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  const onModelSubCategoryChange = t29;
  let t30;
  if ($[43] !== listMonth || $[44] !== onChangeMonth) {
    t30 = value_9 => {
      const matched = listMonth.filter(o_1 => o_1.value === value_9);
      if (matched.length) {
        safeCall(onChangeMonth, matched[0]);
      }
    };
    $[43] = listMonth;
    $[44] = onChangeMonth;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  const onMonthChange = t30;
  let t31;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <h1 className="font-noto-bold text-sub-h2">คำนวณค่างวด</h1>;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  const t32 = modelCategory?.documentId;
  let t33;
  if ($[47] !== isOpenCategory || $[48] !== listModelCategory || $[49] !== modelCategory || $[50] !== onSelectCategory || $[51] !== t32 || $[52] !== toggleCategory) {
    t33 = <ModelCategorySelection isOpen={isOpenCategory} toggle={toggleCategory} list={listModelCategory} value={t32} selectedModelCategory={modelCategory} onSelectedList={onSelectCategory} />;
    $[47] = isOpenCategory;
    $[48] = listModelCategory;
    $[49] = modelCategory;
    $[50] = onSelectCategory;
    $[51] = t32;
    $[52] = toggleCategory;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Divider size="none" />;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  if ($[55] !== listModelSubCategory || $[56] !== modelSubCategory || $[57] !== onModelSubCategoryChange) {
    t35 = !!modelSubCategory && !!listModelSubCategory && listModelSubCategory.length > 0 && <><Radio {...createDataAttributes({
        test: "model-sub-category"
      })} name="model-sub-category" options={listModelSubCategory.map(o_2 => ({
        label: o_2.nameEn,
        value: o_2.documentId,
        checked: o_2.documentId === modelSubCategory.documentId
      }))} onChange={onModelSubCategoryChange} /><Divider size="none" /></>;
    $[55] = listModelSubCategory;
    $[56] = modelSubCategory;
    $[57] = onModelSubCategoryChange;
    $[58] = t35;
  } else {
    t35 = $[58];
  }
  let t36;
  if ($[59] !== isOpenModel || $[60] !== listModel || $[61] !== model || $[62] !== modelSubCategory || $[63] !== onSelectModel || $[64] !== toggleModel) {
    t36 = <GradeSelection isOpen={isOpenModel} toggle={toggleModel} modelSubCategory={modelSubCategory} listModel={listModel} model={model} onSelectModel={onSelectModel} />;
    $[59] = isOpenModel;
    $[60] = listModel;
    $[61] = model;
    $[62] = modelSubCategory;
    $[63] = onSelectModel;
    $[64] = toggleModel;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  let t37;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Divider size="none" />;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] !== color || $[68] !== isOpenColor || $[69] !== listColor || $[70] !== model || $[71] !== onSelectColor || $[72] !== toggleColor) {
    t38 = !!color && <ColorSelection model={model} colors={listColor} isOpen={isOpenColor} toggle={toggleColor} color={color} onSelectColor={onSelectColor} />;
    $[67] = color;
    $[68] = isOpenColor;
    $[69] = listColor;
    $[70] = model;
    $[71] = onSelectColor;
    $[72] = toggleColor;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Divider size="none" />;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  if ($[75] !== percent) {
    t40 = formatPercent(percent);
    $[75] = percent;
    $[76] = t40;
  } else {
    t40 = $[76];
  }
  let t41;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = createDataAttributes({
      test: "down-payment-percent"
    });
    $[77] = t41;
  } else {
    t41 = $[77];
  }
  let t42;
  if ($[78] !== onPercentBlur || $[79] !== onPercentChange || $[80] !== t40) {
    t42 = <TextField pattern="[0-9]*" inputMode="decimal" type="text" name="down_payment_percent" placeholder="0" value={t40} onChange={onPercentChange} onBlur={onPercentBlur} suffix="%" className="max-w-28 min-w-28" {...t41} />;
    $[78] = onPercentBlur;
    $[79] = onPercentChange;
    $[80] = t40;
    $[81] = t42;
  } else {
    t42 = $[81];
  }
  let t43;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <span>หรือ</span>;
    $[82] = t43;
  } else {
    t43 = $[82];
  }
  let t44;
  if ($[83] !== payment) {
    t44 = formatPayment(payment);
    $[83] = payment;
    $[84] = t44;
  } else {
    t44 = $[84];
  }
  let t45;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = createDataAttributes({
      test: "down-payment-amount"
    });
    $[85] = t45;
  } else {
    t45 = $[85];
  }
  let t46;
  if ($[86] !== onPaymentBlur || $[87] !== onPaymentChange || $[88] !== t44) {
    t46 = <TextField pattern="[0-9]*" inputMode="decimal" type="text" name="down_payment_amount" placeholder="0" value={t44} onChange={onPaymentChange} onBlur={onPaymentBlur} suffix="THB" className="max-w-40 min-w-40" {...t45} />;
    $[86] = onPaymentBlur;
    $[87] = onPaymentChange;
    $[88] = t44;
    $[89] = t46;
  } else {
    t46 = $[89];
  }
  let t47;
  if ($[90] !== t42 || $[91] !== t46) {
    t47 = <CustomLabelContent label={"\u0E08\u0E33\u0E19\u0E27\u0E19\u0E40\u0E07\u0E34\u0E19\u0E14\u0E32\u0E27\u0E19\u0E4C"} content={<div className="flex flex-wrap items-center justify-start gap-x-4 gap-y-3">{t42}{t43}{t46}</div>} />;
    $[90] = t42;
    $[91] = t46;
    $[92] = t47;
  } else {
    t47 = $[92];
  }
  let t48;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Divider size="none" />;
    $[93] = t48;
  } else {
    t48 = $[93];
  }
  let t49;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <>ระยะเวลาการผ่อนชำระ<span className="font-noto text-gray-400"> (เดือน)</span></>;
    $[94] = t49;
  } else {
    t49 = $[94];
  }
  let t50;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = createDataAttributes({
      test: "month"
    });
    $[95] = t50;
  } else {
    t50 = $[95];
  }
  let t51;
  if ($[96] !== listMonth || $[97] !== month) {
    let t52;
    if ($[99] !== month) {
      t52 = o_3 => ({
        label: o_3.label,
        value: o_3.value,
        checked: o_3.value === month.value
      });
      $[99] = month;
      $[100] = t52;
    } else {
      t52 = $[100];
    }
    t51 = listMonth.map(t52);
    $[96] = listMonth;
    $[97] = month;
    $[98] = t51;
  } else {
    t51 = $[98];
  }
  let t52;
  if ($[101] !== onMonthChange || $[102] !== t51) {
    t52 = <CustomLabelContent label={t49} content={<Radio {...t50} name="month" options={t51} onChange={onMonthChange} />} />;
    $[101] = onMonthChange;
    $[102] = t51;
    $[103] = t52;
  } else {
    t52 = $[103];
  }
  let t53;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <Divider size="none" />;
    $[104] = t53;
  } else {
    t53 = $[104];
  }
  let t54;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = createDataAttributes({
      test: "interest-type"
    });
    $[105] = t54;
  } else {
    t54 = $[105];
  }
  let t55;
  if ($[106] !== interestType.value) {
    t55 = o_5 => ({
      label: o_5.label,
      value: o_5.value,
      checked: o_5.value === interestType.value
    });
    $[106] = interestType.value;
    $[107] = t55;
  } else {
    t55 = $[107];
  }
  const t56 = "flex flex-wrap gap-x-4";
  const T0 = Radio;
  let t57;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = createDataAttributes({
      test: "interest-type"
    });
    $[108] = t57;
  } else {
    t57 = $[108];
  }
  const t58 = "interest_type";
  let t59;
  if ($[109] !== interestType.value) {
    t59 = o_7 => ({
      label: o_7.label,
      value: o_7.value,
      checked: o_7.value === interestType.value
    });
    $[109] = interestType.value;
    $[110] = t59;
  } else {
    t59 = $[110];
  }
  const t60 = listInterestType.filter(_temp2).map(t59);
  let t61;
  if ($[111] !== T0 || $[112] !== onInterestChange || $[113] !== t57 || $[114] !== t60) {
    t61 = <T0 {...t57} name={t58} options={t60} onChange={onInterestChange} />;
    $[111] = T0;
    $[112] = onInterestChange;
    $[113] = t57;
    $[114] = t60;
    $[115] = t61;
  } else {
    t61 = $[115];
  }
  const t62 = interestType.value === INTEREST_TYPE_TIL;
  let t63;
  if ($[116] !== interest) {
    t63 = formatPercent(interest);
    $[116] = interest;
    $[117] = t63;
  } else {
    t63 = $[117];
  }
  const t64 = parseFloat(interest) >= MAX_INTEREST;
  const t65 = parseFloat(interest) <= 0;
  const t66 = interestType.value !== INTEREST_TYPE_TIL;
  let t67;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = createDataAttributes({
      test: "interest-rate"
    });
    $[118] = t67;
  } else {
    t67 = $[118];
  }
  let t68;
  if ($[119] !== onBlurInterest || $[120] !== onInterestDown || $[121] !== onInterestRateFieldChange || $[122] !== onInterestUp || $[123] !== t62 || $[124] !== t63 || $[125] !== t64 || $[126] !== t65 || $[127] !== t66) {
    t68 = <TextField disabled={t62} pattern="[0-9]*" inputMode="decimal" type="text" name="interest_rate" value={t63} onChange={onInterestRateFieldChange} onBlur={onBlurInterest} suffix="%" spinButton={true} spinUpDisabled={t64} onSpinUp={onInterestUp} spinDownDisabled={t65} onSpinDown={onInterestDown} className="group max-w-32 min-w-32" spinActive={t66} {...t67} />;
    $[119] = onBlurInterest;
    $[120] = onInterestDown;
    $[121] = onInterestRateFieldChange;
    $[122] = onInterestUp;
    $[123] = t62;
    $[124] = t63;
    $[125] = t64;
    $[126] = t65;
    $[127] = t66;
    $[128] = t68;
  } else {
    t68 = $[128];
  }
  let t69;
  if ($[129] !== t61 || $[130] !== t68) {
    t69 = <div className={t56}>{t61}{t68}</div>;
    $[129] = t61;
    $[130] = t68;
    $[131] = t69;
  } else {
    t69 = $[131];
  }
  let t70;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <span className="text-caption text-gray-700" {...createDataAttributes({
      test: "interest-remark"
    })}>หมายเหตุ: ดอกเบี้ยเป็นเพียงค่าประมาณเพื่อช่วยในการตัดสินใจ ไม่สามารถนำมาใช้จริงได้</span>;
    $[132] = t70;
  } else {
    t70 = $[132];
  }
  let t71;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <Divider size="none" />;
    $[133] = t71;
  } else {
    t71 = $[133];
  }
  let t72;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = createDataAttributes({
      test: "button-confirm-calculator"
    });
    $[134] = t72;
  } else {
    t72 = $[134];
  }
  let t73;
  if ($[135] !== onSubmit) {
    t73 = <Button width="full" onClick={onSubmit} {...t72}>คำนวณ</Button>;
    $[135] = onSubmit;
    $[136] = t73;
  } else {
    t73 = $[136];
  }
  let t74;
  if ($[137] !== sentinelRef || $[138] !== t73) {
    t74 = <div ref={sentinelRef} className="lg:hidden">{t73}</div>;
    $[137] = sentinelRef;
    $[138] = t73;
    $[139] = t74;
  } else {
    t74 = $[139];
  }
  return <div className="size-full lg:overflow-y-auto"><div className="table min-w-full"><div className="grid w-full gap-y-4 px-6 pt-10 pb-2">{t31}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t47}{t48}{t52}{t53}<CustomLabelContent label={"\u0E2D\u0E31\u0E15\u0E23\u0E32\u0E14\u0E2D\u0E01\u0E40\u0E1A\u0E35\u0E49\u0E22"} content={<><div className="flex flex-wrap gap-x-4"><Radio {...t54} name="interest_type" options={listInterestType.filter(_temp).map(t55)} onChange={onInterestChange} />{t69}</div>{t70}</>} />{t71}{t74}</div></div></div>;
};
function _temp(o_4) {
  return o_4.value === INTEREST_TYPE_TIL;
}
function _temp2(o_6) {
  return o_6.value !== INTEREST_TYPE_TIL;
}